// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const classnames = require('classnames');
const debounce = require('lodash.debounce');
const useTranslate = require('stremio/common/useTranslate');
const { useStreamingServer, useNotifications, withCoreSuspender, getVisibleChildrenRange, useProfile } = require('stremio/common');
const { ContinueWatchingItem, EventModal, MainNavBars, MetaItem, MetaRow } = require('stremio/components');
const useBoard = require('./useBoard');
const useContinueWatchingPreview = require('./useContinueWatchingPreview');
const styles = require('./styles');
const { default: StreamingServerWarning } = require('./StreamingServerWarning');
const { Link } = require('react-router-dom');

const THRESHOLD = 5;

const Board = () => {
    const t = useTranslate();
    const streamingServer = useStreamingServer();
    const continueWatchingPreview = useContinueWatchingPreview();
    const [board, loadBoardRows] = useBoard();
    const [mobileFilter, setMobileFilter] = React.useState('all');
    const compactQuery = '(max-width: 640px), (max-width: 1000px) and (max-height: 500px) and (orientation: landscape)';
    const [compact, setCompact] = React.useState(() => window.matchMedia(compactQuery).matches);
    React.useEffect(() => {
        const query = window.matchMedia(compactQuery);
        const update = () => setCompact(query.matches);
        query.addEventListener('change', update);
        return () => query.removeEventListener('change', update);
    }, []);
    const visibleCatalogs = React.useMemo(() => {
        if (!compact) return board.catalogs.map((catalog, index) => ({ catalog, index }));
        const matching = board.catalogs.map((catalog, index) => ({ catalog, index })).filter(({ catalog }) => catalog.type === 'movie' || catalog.type === 'series');
        const filtered = mobileFilter === 'all' ? matching : matching.filter(({ catalog }) => catalog.type === mobileFilter);
        return (filtered.length ? filtered : matching.length ? matching : board.catalogs.map((catalog, index) => ({ catalog, index }))).slice(0, 4);
    }, [board.catalogs, compact, mobileFilter]);
    const mobileFilterOptions = React.useMemo(() => [
        { value: 'all', label: 'All' },
        { value: 'movie', label: 'Movies' },
        { value: 'series', label: 'Series' }
    ].filter((option) => option.value === 'all' || board.catalogs.some((catalog) => catalog.type === option.value)), [board.catalogs]);
    const notifications = useNotifications();
    const profile = useProfile();
    const boardCatalogsOffset = continueWatchingPreview.items.length > 0 ? 1 : 0;
    const scrollContainerRef = React.useRef();
    const showStreamingServerWarning = React.useMemo(() => {
        return streamingServer.settings !== null && streamingServer.settings.type === 'Err' && (
            isNaN(profile.settings.streamingServerWarningDismissed.getTime()) ||
            profile.settings.streamingServerWarningDismissed.getTime() < Date.now());
    }, [profile.settings, streamingServer.settings]);
    const onVisibleRangeChange = React.useCallback(() => {
        if (compact) {
            loadBoardRows({ start: 0, end: (visibleCatalogs[visibleCatalogs.length - 1]?.index ?? 0) + 1 });
            return;
        }
        const range = getVisibleChildrenRange(scrollContainerRef.current);
        if (range === null) {
            return;
        }

        const start = Math.max(0, range.start - boardCatalogsOffset - THRESHOLD);
        const end = range.end - boardCatalogsOffset + THRESHOLD;
        if (end < start) {
            return;
        }

        loadBoardRows({ start, end });
    }, [boardCatalogsOffset, compact, visibleCatalogs, loadBoardRows]);
    const onScroll = React.useCallback(debounce(onVisibleRangeChange, 250), [onVisibleRangeChange]);
    React.useLayoutEffect(() => {
        onVisibleRangeChange();
    }, [board.catalogs, onVisibleRangeChange]);
    return (
        <div className={styles['board-container']}>
            <EventModal />
            <MainNavBars className={styles['board-content-container']} route={'board'}>
                <div ref={scrollContainerRef} className={styles['board-content']} onScroll={onScroll}>
                    <div className={styles['mobile-intro']}><span className={styles['mobile-kicker']}>YOUR MOVIE NIGHT STARTS HERE</span><h1>What are we<br/><em>watching?</em></h1><p>Pick a story, then bring your people along.</p></div>
                    {compact && showStreamingServerWarning && <StreamingServerWarning className={styles['board-warning-container']} />}
                    {
                        continueWatchingPreview.items.length > 0 ?
                            <MetaRow
                                className={classnames(styles['board-row'], styles['continue-watching-row'], 'animation-fade-in')}
                                title={t.string('BOARD_CONTINUE_WATCHING')}
                                catalog={continueWatchingPreview}
                                itemComponent={ContinueWatchingItem}
                                notifications={notifications}
                            />
                            :
                            null
                    }
                    {compact && mobileFilterOptions.length > 1 && <div className={styles['mobile-filter']} role="group" aria-label="Browse by type">
                        {mobileFilterOptions.map(({ value, label }) => <button key={value} type="button" aria-pressed={mobileFilter === value} onClick={() => setMobileFilter(value)}>{label}</button>)}
                    </div>}
                    {visibleCatalogs.map(({ catalog, index }) => {
                        switch (catalog.content?.type) {
                            case 'Ready': {
                                return (
                                    <MetaRow
                                        key={index}
                                        className={classnames(styles['board-row'], styles[`board-row-${catalog.content.content[0].posterShape}`], 'animation-fade-in')}
                                        catalog={catalog}
                                        itemComponent={MetaItem}
                                    />
                                );
                            }
                            case 'Err': {
                                if (catalog.content.content !== 'EmptyContent') {
                                    return (
                                        <MetaRow
                                            key={index}
                                            className={classnames(styles['board-row'], 'animation-fade-in')}
                                            catalog={catalog}
                                            message={catalog.content.content}
                                        />
                                    );
                                }
                                return null;
                            }
                            default: {
                                return (
                                    <MetaRow.Placeholder
                                        key={index}
                                        className={classnames(styles['board-row'], styles['board-row-poster'], 'animation-fade-in')}
                                        catalog={catalog}
                                        title={t.catalogTitle(catalog)}
                                    />
                                );
                            }
                        }
                    })}
                    <Link className={styles['mobile-browse-link']} to="/discover">Explore the full catalog <span aria-hidden="true">→</span></Link>
                </div>
            </MainNavBars>
            {
                showStreamingServerWarning && !compact ?
                    <StreamingServerWarning className={styles['board-warning-container']} />
                    :
                    null
            }
        </div>
    );
};

const BoardFallback = () => (
    <div className={styles['board-container']}>
        <MainNavBars className={styles['board-content-container']} route={'board'} />
    </div>
);

module.exports = withCoreSuspender(Board, BoardFallback);
